import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { userAPI } from '../utils/api';
import Avatar from './Avatar';
import Loading from './Loading';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';

interface PublicProfileData {
    user: {
        id: string;
        username: string;
        displayName?: string;
        avatarUrl?: string;
        bio?: string;
        location?: string;
        websiteUrl?: string;
        createdAt: string;
    };
    stats: {
        totalServers: number;
        activeServers: number;
        averageServerRating: number;
        totalComments: number;
        memberSince: string;
    };
}

const PublicProfile: React.FC = () => {
    const { identifier } = useParams<{ identifier: string }>();
    const navigate = useNavigate();
    const { t } = useLanguage();
    const { user: currentUser } = useAuth();
    const [profile, setProfile] = useState<PublicProfileData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchProfile = async () => {
            if (!identifier) {
                setError(t('profile.public.not_found') || 'Profile not found');
                setLoading(false);
                return;
            }

            // If viewing own profile, redirect to /profile
            if (currentUser && (currentUser.id === identifier || currentUser.username === identifier)) {
                navigate('/profile', { replace: true });
                return;
            }

            try {
                setLoading(true);
                const data = await userAPI.getPublicProfile(identifier);
                setProfile(data);
                setError(null);
            } catch (err) {
                console.error('Failed to load public profile:', err);
                setError(t('profile.public.error_loading') || 'Failed to load profile');
            } finally {
                setLoading(false);
            }
        };

        fetchProfile();
    }, [identifier, currentUser, navigate, t]);

    if (loading) {
        return <Loading message={t('common.loading')} />;
    }

    if (error || !profile) {
        return (
            <div className="container mx-auto py-16 px-4 text-center">
                <div className="bg-gray-800 rounded-2xl p-12 max-w-md mx-auto border border-gray-700">
                    <div className="text-6xl mb-4">👤</div>
                    <h2 className="text-2xl font-bold text-gray-300 mb-2">
                        {t('profile.public.not_found') || 'Profile not found'}
                    </h2>
                    <p className="text-gray-500 mb-6">
                        {error || (t('profile.public.not_found_desc') || 'This user does not exist or has been removed.')}
                    </p>
                    <button
                        onClick={() => navigate('/')}
                        className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-6 py-2 rounded-xl font-semibold transition-all"
                    >
                        {t('server.details.back_home') || 'Back to Home'}
                    </button>
                </div>
            </div>
        );
    }

    const { user, stats } = profile;
    const displayName = user.displayName || user.username;

    return (
        <div className="container mx-auto py-10 px-4">
            <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl shadow-2xl p-8 max-w-3xl mx-auto border border-gray-700">
                {/* Header */}
                <div className="flex flex-col md:flex-row items-center gap-6 mb-8">
                    <Avatar src={user.avatarUrl} alt={displayName} size="xl" />
                    <div className="text-center md:text-left">
                        <h1 className="text-3xl font-bold text-white mb-1">{displayName}</h1>
                        <p className="text-gray-400">@{user.username}</p>
                        {user.location && (
                            <div className="flex items-center justify-center md:justify-start gap-2 mt-2 text-gray-500">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                                <span>{user.location}</span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Bio */}
                {user.bio && (
                    <div className="mb-8 p-4 bg-gray-900/50 rounded-xl border border-gray-700">
                        <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-2">
                            {t('profile.bio') || 'Bio'}
                        </h3>
                        <p className="text-gray-300 whitespace-pre-wrap">{user.bio}</p>
                    </div>
                )}

                {/* Website */}
                {user.websiteUrl && (
                    <div className="mb-8">
                        <a
                            href={user.websiteUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 text-blue-400 hover:text-blue-300 transition-colors"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                            </svg>
                            {user.websiteUrl.replace(/^https?:\/\//, '')}
                        </a>
                    </div>
                )}

                {/* Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-gray-900/60 border border-gray-700 rounded-xl p-4 text-center">
                        <div className="text-2xl font-bold text-blue-400">{stats.totalServers}</div>
                        <div className="text-gray-400 text-xs mt-1">{t('profile.stats.servers_created') || 'Servers'}</div>
                    </div>
                    <div className="bg-gray-900/60 border border-gray-700 rounded-xl p-4 text-center">
                        <div className="text-2xl font-bold text-green-400">{stats.activeServers}</div>
                        <div className="text-gray-400 text-xs mt-1">{t('profile.stats.active_servers') || 'Active'}</div>
                    </div>
                    <div className="bg-gray-900/60 border border-gray-700 rounded-xl p-4 text-center">
                        <div className="text-2xl font-bold text-purple-400">{stats.averageServerRating.toFixed(1)}</div>
                        <div className="text-gray-400 text-xs mt-1">{t('profile.stats.avg_rating') || 'Avg Rating'}</div>
                    </div>
                    <div className="bg-gray-900/60 border border-gray-700 rounded-xl p-4 text-center">
                        <div className="text-2xl font-bold text-amber-400">{stats.totalComments}</div>
                        <div className="text-gray-400 text-xs mt-1">{t('profile.stats.comments') || 'Comments'}</div>
                    </div>
                </div>

                {/* Member Since */}
                <div className="mt-6 text-center text-gray-500 text-sm">
                    {t('profile.public.member_since') || 'Member since'} {new Date(stats.memberSince).toLocaleDateString()}
                </div>
            </div>
        </div>
    );
};

export default PublicProfile;
